/**
 * Forecasting engine (Blueprint 4.4, release 1). Forecasts are estimates, so the
 * rules here are about being transparent and internally exact:
 * - every assumption is explicit data (stored with the forecast, so it can be reproduced);
 * - each month or week is rounded to the cent once; totals are sums of what is shown;
 * - cash rolls forward exactly: start + inflows − outflows = end, every period;
 * - money spread across weeks splits to the cent with nothing lost.
 */
import type { Cents } from "../money";
import { periodLabel } from "./dashboard";
import { Exact } from "./exact";

// --- helpers ---------------------------------------------------------------------------------

const pct = (s: string) => Exact.decimal(s).div(Exact.int(100));

/** Splits an amount across n parts that add back exactly (earlier parts take the extra cents). */
export function splitEvenly(total: Cents, n: number): Cents[] {
  if (!Number.isInteger(n) || n <= 0) throw new RangeError("n must be a positive whole number");
  const sign = total < 0 ? -1 : 1;
  const abs = Math.abs(total);
  const base = Math.floor(abs / n);
  const extra = abs - base * n;
  return Array.from({ length: n }, (_, i) => sign * (base + (i < extra ? 1 : 0)));
}

/** "2027-01" + k months. */
export function addMonths(month: string, k: number): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + k;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

// --- Months until cash runs out ----------------------------------------------------------------

/** What forecast screens call their runway, so it is never confused with the scorecard's "Runway at current burn". */
export const FORECAST_RUNWAY_LABEL = "Months until cash runs out (forecast)";

export type CashOut =
  | { kind: "runs_out"; months: number; firstNegativeMonth: string; display: string }
  | { kind: "lasts"; horizonMonths: number; display: string };

/**
 * Months until cash runs out in a forecast: the whole months of cash before the first forecast month
 * that ends below zero (0 when the first month already does). When no month does, cash lasts the
 * whole horizon. Scorecard runway (cash ÷ average burn) is a different figure; see kpis.ts.
 */
export function monthsUntilCashRunsOut(months: Array<{ month: string; cashEnd: Cents }>): CashOut {
  const k = months.findIndex((m) => m.cashEnd < 0);
  if (k < 0) return { kind: "lasts", horizonMonths: months.length, display: `Lasts the ${months.length} months shown` };
  const first = months[k]!.month;
  return { kind: "runs_out", months: k, firstNegativeMonth: first, display: k === 0 ? `Under 1 month (runs out in ${periodLabel(first)})` : `${k} month${k === 1 ? "" : "s"} (runs out in ${periodLabel(first)})` };
}

// --- 12-month P&L forecast ---------------------------------------------------------------------

/** Trailing actuals the forecast starts from (monthly averages, cents). */
export type Baseline = {
  revenue: Cents;
  /** Cost of goods sold as a share of revenue, exact text such as "0.42". */
  cogsShare: string;
  /** Operating expenses per month (excluding cost of goods sold). */
  operatingExpenses: Cents;
  /** Other income − other expenses per month. */
  otherNet: Cents;
  /** Non-cash depreciation & amortization per month (added back for cash). */
  depreciation: Cents;
};

export type Assumptions = {
  /** Revenue growth per month, compounded, in percent ("1.5" = 1.5%). */
  monthlyGrowthPct: string;
  /** Optional change to the cost-of-goods share, in percentage points ("-1" = one point lower). */
  cogsSharePointsChange: string;
  /** Price change applied to revenue from a month onward. */
  priceChanges: Array<{ month: string; pct: string }>;
  newHires: Array<{ label: string; startMonth: string; monthlyCost: Cents }>;
  oneTime: Array<{ label: string; month: string; amount: Cents; kind: "income" | "expense" }>;
  /** New loans: interest each month is balance × annual rate ÷ 12 (rounded to the cent); the rest of the payment is principal. */
  loans: Array<{ label: string; month: string; proceeds: Cents; monthlyPayment: Cents; payments: number; annualRatePct?: string }>;
};

export const EMPTY_ASSUMPTIONS: Assumptions = { monthlyGrowthPct: "0", cogsSharePointsChange: "0", priceChanges: [], newHires: [], oneTime: [], loans: [] };

export type ForecastMonth = {
  month: string;
  revenue: Cents;
  cogs: Cents;
  grossProfit: Cents;
  operatingExpenses: Cents;
  otherNet: Cents;
  netIncome: Cents;
  /** Cash: net income + depreciation added back + loan proceeds − loan payments. */
  cashChange: Cents;
  cashEnd: Cents;
};

export type PnlForecast = { months: ForecastMonth[]; totals: Omit<ForecastMonth, "month" | "cashEnd">; startingCash: Cents; runwayMonths: number | null; firstNegativeMonth: string | null; cashOut: CashOut };

/**
 * Twelve months starting at `firstMonth`. Revenue for month k (0-based) =
 * baseline × (1 + growth)^(k+1) × Π(1 + price changes in effect), rounded to the cent.
 */
export function forecastPnl(firstMonth: string, baseline: Baseline, a: Assumptions, startingCash: Cents): PnlForecast {
  const growth = Exact.int(1).add(pct(a.monthlyGrowthPct));
  const cogsShare = Exact.decimal(baseline.cogsShare).add(pct(a.cogsSharePointsChange));
  if (cogsShare.sign() < 0) throw new RangeError("Cost of goods share can't be negative");
  let factor = Exact.int(1);
  let cash = startingCash;
  const months: ForecastMonth[] = [];
  const balances: number[] = a.loans.map(() => 0);
  for (let k = 0; k < 12; k++) {
    const month = addMonths(firstMonth, k);
    factor = factor.mul(growth);
    let price = Exact.int(1);
    for (const p of a.priceChanges) if (p.month <= month) price = price.mul(Exact.int(1).add(pct(p.pct)));
    const oneTimeIncome = a.oneTime.filter((o) => o.month === month && o.kind === "income").reduce((s, o) => s + o.amount, 0);
    const oneTimeExpense = a.oneTime.filter((o) => o.month === month && o.kind === "expense").reduce((s, o) => s + o.amount, 0);
    const revenue = Exact.int(baseline.revenue).mul(factor).mul(price).toCents() + oneTimeIncome;
    // Cost of goods follows volume (growth), not price: a price rise doesn't make goods cost more.
    const cogs = Exact.int(baseline.revenue).mul(factor).mul(cogsShare).toCents();
    const hires = a.newHires.filter((h) => h.startMonth <= month).reduce((s, h) => s + h.monthlyCost, 0);
    const operatingExpenses = baseline.operatingExpenses + hires + oneTimeExpense;
    const grossProfit = revenue - cogs;
    let loanCash = 0;
    let interest = 0;
    for (const [li, l] of a.loans.entries()) {
      if (l.month === month) {
        loanCash += l.proceeds;
        balances[li] = l.proceeds;
      }
      const k2 = monthIndex(l.month, month);
      if (k2 >= 1 && k2 <= l.payments && balances[li]! > 0) {
        const int = l.annualRatePct ? Exact.int(balances[li]!).mul(pct(l.annualRatePct)).div(Exact.int(12)).toCents() : 0;
        const payment = Math.min(l.monthlyPayment, balances[li]! + int);
        interest += int;
        balances[li] = balances[li]! - (payment - int);
        loanCash -= payment;
      }
    }
    // Interest is an expense (other expense); the whole payment leaves cash.
    const netIncome = grossProfit - operatingExpenses + baseline.otherNet - interest;
    const cashChange = netIncome + interest + baseline.depreciation + loanCash;
    cash += cashChange;
    months.push({ month, revenue, cogs, grossProfit, operatingExpenses, otherNet: baseline.otherNet - interest, netIncome, cashChange, cashEnd: cash });
  }
  const sum = (f: (m: ForecastMonth) => Cents) => months.reduce((s, m) => s + f(m), 0);
  const cashOut = monthsUntilCashRunsOut(months);
  return {
    months,
    totals: {
      revenue: sum((m) => m.revenue),
      cogs: sum((m) => m.cogs),
      grossProfit: sum((m) => m.grossProfit),
      operatingExpenses: sum((m) => m.operatingExpenses),
      otherNet: sum((m) => m.otherNet),
      netIncome: sum((m) => m.netIncome),
      cashChange: sum((m) => m.cashChange),
    },
    startingCash,
    // Whole months of cash before the first negative month-end; null = lasts beyond the 12 months.
    runwayMonths: cashOut.kind === "runs_out" ? cashOut.months : null,
    firstNegativeMonth: cashOut.kind === "runs_out" ? cashOut.firstNegativeMonth : null,
    cashOut,
  };
}

function monthIndex(from: string, to: string): number {
  const [y1, m1] = from.split("-").map(Number) as [number, number];
  const [y2, m2] = to.split("-").map(Number) as [number, number];
  return (y2 - y1) * 12 + (m2 - m1);
}

export type ScenarioName = "base" | "best" | "worst";

/** Best and worst start from base and shift growth and cost of goods; staff can change every number. */
export function defaultScenarios(base: Assumptions): Record<ScenarioName, Assumptions> {
  const shift = (s: string, by: number) => Exact.decimal(s).add(Exact.int(by)).toFixed(2);
  return {
    base,
    best: { ...base, monthlyGrowthPct: shift(base.monthlyGrowthPct, 1), cogsSharePointsChange: shift(base.cogsSharePointsChange, -1) },
    worst: { ...base, monthlyGrowthPct: shift(base.monthlyGrowthPct, -2), cogsSharePointsChange: shift(base.cogsSharePointsChange, 2) },
  };
}

// --- 13-week cash forecast ---------------------------------------------------------------------

export type AgingBuckets = { current: Cents; d1_30: Cents; d31_60: Cents; d61_90: Cents; d91_plus: Cents };

/** For each receivables bucket: share expected to be collected, and the weeks (1–13) it arrives in, evenly. */
export type CollectionRule = { pct: string; fromWeek: number; toWeek: number };
export type CashAssumptions = {
  collections: Record<keyof AgingBuckets, CollectionRule>;
  /** Payables: current ones paid evenly over these weeks; overdue ones in week 1. */
  payablesCurrentWeeks: number;
  /** Semimonthly pay days default to the 15th and the last day; a pay day on a weekend moves to the Friday before. */
  payroll: { amountPerRun: Cents; frequency: "weekly" | "biweekly" | "semimonthly" | "monthly"; nextPayDate: string; semimonthlyDays?: [number, number] } | null;
  recurring: Array<{ label: string; amount: Cents; direction: "in" | "out"; frequency: "weekly" | "monthly"; startDate: string }>;
  /** New sales collected each week once the first ones are paid (weekly amount, starting week). */
  newSales: { weeklyAmount: Cents; fromWeek: number } | null;
};

export const DEFAULT_COLLECTIONS: CashAssumptions["collections"] = {
  current: { pct: "100", fromWeek: 1, toWeek: 5 },
  d1_30: { pct: "95", fromWeek: 1, toWeek: 3 },
  d31_60: { pct: "80", fromWeek: 2, toWeek: 6 },
  d61_90: { pct: "50", fromWeek: 4, toWeek: 8 },
  d91_plus: { pct: "0", fromWeek: 1, toWeek: 1 },
};

export type CashWeek = { week: number; start: string; end: string; opening: Cents; inflows: Array<{ label: string; amount: Cents }>; outflows: Array<{ label: string; amount: Cents }>; net: Cents; closing: Cents };

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Dates a recurring item or payroll falls on within [start, end]. */
function occurrences(first: string, frequency: "weekly" | "biweekly" | "semimonthly" | "monthly", start: string, end: string, days: [number, number] = [15, 31]): string[] {
  const out: string[] = [];
  if (frequency === "semimonthly") {
    // Two days each month (31 = the last day); the first one is never before the next pay date.
    for (let m = first.slice(0, 7); `${m}-01` <= end; m = addMonths(m, 1)) {
      const lastDay = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0)).getUTCDate();
      for (const day of days) {
        const d = `${m}-${String(Math.min(day, lastDay)).padStart(2, "0")}`;
        if (d >= first && d >= start && d <= end) out.push(d);
      }
    }
    return out;
  }
  let d = first;
  let i = 0;
  while (d <= end && i < 1000) {
    if (d >= start) out.push(d);
    i++;
    if (frequency === "monthly") {
      const [y, mo, day] = first.split("-").map(Number) as [number, number, number];
      const target = new Date(Date.UTC(y, mo - 1 + i, 1));
      const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
      target.setUTCDate(Math.min(day, lastDay));
      d = target.toISOString().slice(0, 10);
    } else d = addDays(first, i * (frequency === "weekly" ? 7 : 14));
  }
  return out;
}

/** Thirteen weeks starting on `startDate` (week 1 = startDate … +6 days). */
export function forecastCash13(startDate: string, openingCash: Cents, ar: AgingBuckets, ap: { current: Cents; overdue: Cents }, a: CashAssumptions): { weeks: CashWeek[]; lowestClosing: Cents; lowestWeek: number } {
  const weeks: CashWeek[] = Array.from({ length: 13 }, (_, i) => ({ week: i + 1, start: addDays(startDate, i * 7), end: addDays(startDate, i * 7 + 6), opening: 0, inflows: [], outflows: [], net: 0, closing: 0 }));
  const LABELS: Record<keyof AgingBuckets, string> = { current: "Receivables (current)", d1_30: "Receivables 1–30 days", d31_60: "Receivables 31–60 days", d61_90: "Receivables 61–90 days", d91_plus: "Receivables over 90 days" };
  for (const key of Object.keys(LABELS) as Array<keyof AgingBuckets>) {
    const rule = a.collections[key];
    if (rule.fromWeek < 1 || rule.toWeek > 13 || rule.fromWeek > rule.toWeek) throw new RangeError("Collection weeks must be within 1–13");
    const expected = Exact.int(ar[key]).mul(pct(rule.pct)).toCents();
    if (!expected) continue;
    splitEvenly(expected, rule.toWeek - rule.fromWeek + 1).forEach((amt, i) => weeks[rule.fromWeek - 1 + i]!.inflows.push({ label: LABELS[key], amount: amt }));
  }
  if (ap.overdue) weeks[0]!.outflows.push({ label: "Overdue payables", amount: ap.overdue });
  if (ap.current) splitEvenly(ap.current, a.payablesCurrentWeeks).forEach((amt, i) => weeks[i]!.outflows.push({ label: "Payables", amount: amt }));
  const last = weeks[12]!.end;
  if (a.payroll) {
    for (const raw of occurrences(a.payroll.nextPayDate, a.payroll.frequency, startDate, last, a.payroll.semimonthlyDays)) {
      const d = fridayIfWeekend(raw);
      const w = Math.floor(daysBetween(startDate, d < startDate ? startDate : d) / 7);
      weeks[w]!.outflows.push({ label: "Payroll", amount: a.payroll.amountPerRun });
    }
  }
  for (const r of a.recurring) {
    for (const d of occurrences(r.startDate, r.frequency, startDate, last)) {
      const w = weeks[Math.floor(daysBetween(startDate, d) / 7)]!;
      (r.direction === "in" ? w.inflows : w.outflows).push({ label: r.label, amount: r.amount });
    }
  }
  if (a.newSales) for (let i = Math.max(1, a.newSales.fromWeek); i <= 13; i++) weeks[i - 1]!.inflows.push({ label: "New sales collected", amount: a.newSales.weeklyAmount });

  let cash = openingCash;
  let lowest = Infinity;
  let lowestWeek = 1;
  for (const w of weeks) {
    w.opening = cash;
    const inn = w.inflows.reduce((s, x) => s + x.amount, 0);
    const out = w.outflows.reduce((s, x) => s + x.amount, 0);
    w.net = inn - out;
    cash += w.net;
    w.closing = cash;
    if (cash < lowest) {
      lowest = cash;
      lowestWeek = w.week;
    }
  }
  return { weeks, lowestClosing: lowest, lowestWeek };
}

/** Banks don't pay on weekends: a Saturday or Sunday pay date moves to the Friday before. */
function fridayIfWeekend(day: string): string {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  return dow === 6 ? addDays(day, -1) : dow === 0 ? addDays(day, -2) : day;
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

// --- forecast accuracy -------------------------------------------------------------------------

/** Error of a forecast against actuals: actual − forecast, and % of the forecast (exact, then displayed). */
export function forecastError(forecast: Cents, actual: Cents): { dollars: Cents; pct: string } {
  const dollars = actual - forecast;
  return { dollars, pct: forecast === 0 ? "—" : `${Exact.int(dollars).div(Exact.int(Math.abs(forecast))).mul(Exact.int(100)).toFixed(1)}%` };
}
